import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  FileText,
  PenLine,
  ChevronRight,
  ChevronLeft,
  Send,
  Loader2,
  AlertCircle,
  Download,
} from 'lucide-react';
import SignaturePad from './SignaturePad';
import FormInput from './FormInput';
import ThemeToggle from './ThemeToggle';
import { toast } from '../utils/toast';
import { generateRateAgreementPdf } from '../utils/generateRateAgreementPdf';
import { AEL_LOGO_DATA_URI } from '../../../lib/aelLogo';
import type { PublicInviteData, RateAgreementLetterFormData, RateAgreementLetterFields, AuditLogEntry } from '../types';

const EMPTY_FIELDS: RateAgreementLetterFields = {
  billToType: 'Bill To/Consignee',
  proposalDate: '',
  letterDate: '',
  billingEmail: '',
  accountNumber: '',
  address: '',
  cityStateZip: '',
  originCityState: 'Vista, CA',
  destinationCity: '',
  destinationState: '',
  destinationZip: '',
  destinationCityState: '',
  consigneeName: '',
  consigneeAccount: '',
  consigneeCity: '',
  consigneeState: '',
  consigneeZip: '',
  outboundState: 'FL',
  dos: '',
  firstShipmentDate: '',
  minimumShipmentAmount: '',
  dollarAmount: '',
  unitOfMeasure: 'Pallet',
  handlingFee: '$0.00',
  palletRate: '',
  cubeRateDetails: '',
  minimumShipmentDetails: '',
  paymentTerms: 'Net 15 Days',
  paymentTimeframe: '',
  lateFeePercentage: '',
  senderEmail: '',
  senderName: '',
};

const STEPS = ['Review Agreement', 'Sign & Submit'];

interface RateAgreementLetterFormProps {
  invite: PublicInviteData;
  onSubmitted?: () => void;
}

export default function RateAgreementLetterForm({ invite, onSubmitted }: RateAgreementLetterFormProps) {
  const [raFields, setRaFields] = useState<RateAgreementLetterFields>(
    invite.rateAgreementFields || EMPTY_FIELDS
  );
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(invite.status === 'completed');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [contractOfCarriageAccepted, setContractOfCarriageAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [printName, setPrintName] = useState(invite.signerName || '');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [ip, setIp] = useState('Unknown');
  const [openedAt] = useState(new Date().toISOString());

  // If DocuSeal signing room exists, render DocuSeal embed
  if (invite.docusealSigningUrl) {
    const embedUrl = invite.metadata?.embedSrc || invite.docusealSigningUrl;
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex flex-col">
        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <img src={AEL_LOGO_DATA_URI} alt="Armellini Express Lines, Inc." className="h-9 object-contain" />
            <div>
              <h1 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Rate Agreement & Service Contract
              </h1>
              <p className="text-xs text-slate-500">
                Prepared for {invite.companyName} • Secure DocuSeal e-Signature
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={invite.docusealSigningUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 rounded-lg transition"
            >
              Open in Full Window
            </a>
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 w-full max-w-5xl mx-auto p-4 md:p-6 flex flex-col">
          <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[750px]">
            <iframe
              src={embedUrl}
              title="Armellini Rate Agreement"
              className="w-full h-full min-h-[750px] border-0"
              allow="camera; microphone; clipboard-read; clipboard-write"
            />
          </div>
        </main>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const [signedDate] = useState(today);

  // Fetch client IP for audit log
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((d) => setIp(d.ip || 'Unknown'))
      .catch(() => {
        // Non-critical fallback
      });
  }, []);

  const validateStep = (s: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (s === 1) {
      if (!agreementAccepted) {
        newErrors.agreementAccepted = 'You must accept the terms of the rate agreement to proceed.';
      }
      if (!contractOfCarriageAccepted) {
        newErrors.contractOfCarriageAccepted = 'You must review and accept the Armellini Contract of Carriage to proceed.';
      }
    }
    if (s === 2) {
      if (!signature) {
        newErrors.signature = 'Signature is required.';
      }
      if (!printName.trim()) {
        newErrors.printName = 'Full printed legal name is required.';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    setTouched((prev) => ({ ...prev, agreementAccepted: true, contractOfCarriageAccepted: true }));
    if (validateStep(1)) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    setErrors({});
    setTouched({});
    setStep(1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setTouched((prev) => ({ ...prev, signature: true, printName: true }));
    if (!validateStep(2)) return;
    setSubmitting(true);

    try {
      const userAgent = navigator.userAgent;
      const nowStr = new Date().toISOString();

      const auditLog: AuditLogEntry[] = [
        {
          action: 'Invite Created',
          timestamp: invite.createdAt || nowStr,
          details: `Rate Agreement invitation sent to ${invite.customerEmail} for ${invite.companyName}`,
          ip: 'System',
          userAgent: 'System',
        },
        {
          action: 'Agreement Opened',
          timestamp: openedAt,
          details: 'Rate Agreement link accessed by recipient',
          ip,
          userAgent,
        },
        {
          action: 'Agreement Reviewed',
          timestamp: nowStr,
          details: `${invite.signerName || printName} reviewed and accepted rate agreement terms`,
          ip,
          userAgent,
        },
        {
          action: 'Signature Captured',
          timestamp: nowStr,
          details: 'Recipient applied authorized signature',
          ip,
          userAgent,
        },
        {
          action: 'Form Submitted',
          timestamp: nowStr,
          details: `Rate Agreement signed and submitted for ${invite.companyName}`,
          ip,
          userAgent,
        },
      ];

      const submittedFormData: RateAgreementLetterFormData = {
        formType: 'rate_agreement_letter',
        fields: raFields,
        signature,
        signedDate,
        printName: printName.trim(),
        agreementAccepted,
        contractOfCarriageAccepted,
      };

      // Generate PDF
      let pdfBase64: string | null = null;
      try {
        pdfBase64 = await generateRateAgreementPdf(
          submittedFormData,
          invite.companyName,
          invite.signerName || printName,
          invite.id,
          invite.customerEmail,
          auditLog
        );
        if (pdfBase64) {
          const byteString = atob(pdfBase64);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(blobUrl);
        }
      } catch (err) {
        console.error('PDF generation failed:', err);
      }

      // Submit to CRM Backend
      const response = await fetch(`/api/public/forms/${invite.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: invite.companyName,
          customerEmail: invite.customerEmail,
          formData: submittedFormData,
          signedPdfBase64: pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null,
          attachments: [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit form');
      }

      setCompleted(true);
      toast.success('Rate Agreement successfully signed and submitted!');
      if (onSubmitted) onSubmitted();
    } catch (err: any) {
      console.error('Submission error:', err);
      toast.error(err.message || 'Failed to submit agreement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white p-10 rounded-3xl shadow-xl text-center space-y-6 border border-slate-100">
          <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={44} />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-slate-900">Agreement Completed</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Thank you, <span className="font-semibold text-slate-900">{invite.companyName}</span>. Your signed Rate Agreement has been recorded and safely stored.
            </p>
          </div>

          {pdfBlobUrl && (
            <div className="pt-2">
              <a
                href={pdfBlobUrl}
                download={`Rate_Agreement_${invite.companyName.replace(/\s+/g, '_')}.pdf`}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl font-medium text-sm transition-all shadow-md"
              >
                <Download size={16} /> Download Copy (PDF)
              </a>
            </div>
          )}
          <p className="text-xs text-slate-400">
            A confirmation has also been dispatched to {invite.customerEmail}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-200">
          <div className="flex items-center gap-4">
            <img src={AEL_LOGO_DATA_URI} alt="Armellini Express Lines, Inc." className="h-10 object-contain" />
            <div>
              <h1 className="text-lg font-bold text-slate-900">Official Rate Agreement Letter</h1>
              <p className="text-xs text-slate-500">{invite.companyName}</p>
            </div>
          </div>
          <ThemeToggle />
        </div>

        {/* Progress Stepper */}
        <div className="flex items-center justify-center gap-4">
          {STEPS.map((stepName, idx) => (
            <div key={stepName} className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step === idx + 1
                    ? 'bg-blue-600 text-white'
                    : step > idx + 1
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {step > idx + 1 ? '✓' : idx + 1}
              </div>
              <span className={`text-xs font-semibold ${step === idx + 1 ? 'text-slate-900' : 'text-slate-400'}`}>
                {stepName}
              </span>
              {idx < STEPS.length - 1 && <div className="w-8 h-0.5 bg-slate-200 ml-2" />}
            </div>
          ))}
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-slate-200/80 space-y-8">
          {step === 1 && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h2 className="text-xl font-bold text-slate-900">Review Rate Terms</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Please review the negotiated freight rates and service terms below before signing.
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 space-y-4 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-slate-400 uppercase font-semibold">
                      {raFields.billToType || 'Bill To/Consignee'}
                    </span>
                    <p className="font-semibold text-slate-900">{invite.companyName}</p>
                    <p className="text-xs text-slate-600">
                      {[raFields.destinationCity, raFields.destinationState].filter(Boolean).join(', ') || raFields.destinationCityState || ''}{' '}
                      {raFields.destinationZip || ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 uppercase font-semibold">Account Number</span>
                    <p className="font-semibold text-slate-900">{raFields.accountNumber || 'New Account'}</p>
                  </div>
                  {raFields.billToType === 'Bill To/Shipper' && (
                    <>
                      <div>
                        <span className="text-xs text-slate-400 uppercase font-semibold">Consignee</span>
                        <p className="font-semibold text-slate-900">{raFields.consigneeName || invite.companyName}</p>
                        <p className="text-xs text-slate-600">
                          {[raFields.consigneeCity, raFields.consigneeState].filter(Boolean).join(', ')}{' '}
                          {raFields.consigneeZip || ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 uppercase font-semibold">Consignee Account Number</span>
                        <p className="font-semibold text-slate-900">{raFields.consigneeAccount || 'New Account'}</p>
                      </div>
                    </>
                  )}
                  <div>
                    <span className="text-xs text-slate-400 uppercase font-semibold">
                      {(raFields.outboundState || 'FL').toUpperCase()} Outbound Day of Service
                    </span>
                    <p className="font-medium text-slate-800">{raFields.dos || 'As Quoted'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 uppercase font-semibold">First Shipment Date</span>
                    <p className="font-medium text-slate-800">{raFields.firstShipmentDate || 'Upon Account Activation'}</p>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-slate-400 uppercase font-semibold">Rate</span>
                    <p className="font-bold text-blue-900">
                      {raFields.dollarAmount
                        ? `${raFields.dollarAmount}/${raFields.unitOfMeasure || 'Pallet'} + ${raFields.handlingFee || '$0.00'} /piece handling fee, + fuel surcharge`
                        : raFields.palletRate || 'As Quoted'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 uppercase font-semibold">Minimum Per Shipment</span>
                    <p className="font-bold text-slate-800">
                      {raFields.minimumShipmentAmount || raFields.minimumShipmentDetails || '$125.00'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 uppercase font-semibold">Payment Terms</span>
                    <p className="font-bold text-slate-800">{raFields.paymentTerms || 'Net 15 Days'}</p>
                  </div>
                </div>
              </div>

              {/* Agreement Acceptance Checkbox */}
              <div className="pt-2">
                <label className="flex items-start gap-3 p-4 rounded-2xl bg-blue-50/50 border border-blue-100 cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div className="text-sm">
                    <span className="font-semibold text-slate-900 block">I Accept Rate Agreement Terms</span>
                    <span className="text-slate-600 text-xs">
                      I have reviewed the rate schedule and agree to the specified rates, freight terms, and payment conditions set forth by Armellini Logistics.
                    </span>
                  </div>
                </label>
                {errors.agreementAccepted && (
                  <p className="text-xs font-medium text-red-500 mt-2">{errors.agreementAccepted}</p>
                )}
              </div>

              {/* Armellini Contract of Carriage Review & Mandatory Acceptance */}
              <div className="pt-2">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 mb-3">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-slate-700 flex-1">
                      <span className="font-bold text-slate-900 block text-sm mb-1">
                        Armellini Contract of Carriage
                      </span>
                      All freight shipments tendered to Armellini Express Lines are governed by the terms, liability rules, and claims provisions specified in our official Contract of Carriage. You must review this document before signing.
                      <div className="mt-2.5">
                        <a
                          href="/docs/Armellini-Contract-of-Carriage.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Review Armellini Contract of Carriage (PDF) &rarr;
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <label className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50/70 border border-slate-200 cursor-pointer hover:bg-slate-100/80 transition-colors">
                  <input
                    type="checkbox"
                    checked={contractOfCarriageAccepted}
                    onChange={(e) => setContractOfCarriageAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div className="text-sm">
                    <span className="font-semibold text-slate-900 block">
                      I have read, accept, and agree to the Armellini Contract of Carriage
                    </span>
                    <span className="text-slate-600 text-xs">
                      I certify that I have opened and reviewed the{' '}
                      <a
                        href="/docs/Armellini-Contract-of-Carriage.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline font-medium hover:text-blue-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Armellini Contract of Carriage
                      </a>{' '}
                      and accept all terms, conditions, and liability policies.
                    </span>
                  </div>
                </label>
                {errors.contractOfCarriageAccepted && (
                  <p className="text-xs font-medium text-red-500 mt-2">{errors.contractOfCarriageAccepted}</p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h2 className="text-xl font-bold text-slate-900">Authorize & Sign</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Apply your digital signature to execute this agreement.
                </p>
              </div>

              <div className="space-y-4">
                <FormInput
                  label="Full Legal Signer Name"
                  value={printName}
                  onChange={setPrintName}
                  placeholder="e.g. John Doe"
                  error={errors.printName}
                />

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider block">
                    Digital Signature <span className="text-red-500">*</span>
                  </label>
                  <SignaturePad
                    value={signature}
                    onChange={setSignature}
                    placeholder="Type legal name or switch to Draw mode..."
                  />
                  {errors.signature && (
                    <p className="text-xs font-medium text-red-500">{errors.signature}</p>
                  )}
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs text-slate-500 space-y-1">
                  <p className="font-semibold text-slate-700">Legal Disclosure & Audit Verification</p>
                  <p>
                    By clicking "Submit Signed Agreement", you consent to be legally bound by this document. Your IP address ({ip}) and timestamp will be permanently cryptographically bound to the signed PDF document for auditing and contract enforcement.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-slate-100">
            {step > 1 ? (
              <button
                type="button"
                onClick={handlePrev}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
            ) : (
              <div />
            )}

            {step === 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-7 py-3 rounded-xl font-semibold text-sm shadow-md transition-all cursor-pointer"
              >
                Proceed to Sign <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-8 py-3.5 rounded-xl font-semibold text-sm shadow-md transition-all cursor-pointer"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Send size={16} /> Submit Signed Agreement
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
